import React from 'react';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface SectionProps {
  id?: string;
  title?: string;
  subtitle?: string;
  className?: string;
  gradient?: "top" | "bottom" | "both" | "none";
  children: React.ReactNode;
  badgeTooltip?: string;
}

const Section: React.FC<SectionProps> = ({ 
  id, 
  title, 
  subtitle, 
  className,
  gradient = "none", 
  children,
  badgeTooltip
}) => {
  const isMobile = useIsMobile();
  
  return (
    <section 
      id={id} 
      className={cn('py-16 md:py-20 scroll-mt-20 relative z-10', className)}
    >
      <motion.div 
        className="container px-4 md:px-6"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: isMobile ? "-50px" : "-100px" }}
        transition={{ duration: 0.7 }}
      >
        {title && (
          <div className="mb-12 md:mb-16 text-center">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="relative inline-block"
            >
              <span className="flex items-center justify-center gap-2">
                <h2 
                  className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2 relative inline-block bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-gray-300"
                >
                  {title}
                </h2>
                {badgeTooltip && (
                  <span className="ml-2 align-middle inline-flex">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          aria-label="Više informacija"
                          className="w-5 h-5 rounded-full bg-white/5 border border-white/20 flex items-center justify-center text-synorio-purple hover:bg-synorio-purple/10 focus:outline-none focus:ring-2 focus:ring-synorio-purple/40 transition-all shadow-sm"
                          tabIndex={0}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="white" fillOpacity="0.10"/><path d="M12 8.5a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5zm-1 2.75c0-.41.34-.75.75-.75h.5c.41 0 .75.34.75.75v4c0 .41-.34.75-.75.75h-.5a.75.75 0 0 1-.75-.75v-4z" fill="currentColor"/></svg>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent side="top" align="center" className="max-w-xs text-center text-sm p-3 bg-gray-900/95 border border-synorio-purple/20 shadow-xl">
                        {badgeTooltip}
                      </PopoverContent>
                    </Popover>
                  </span>
                )}
              </span>
              <motion.div 
                className="absolute -bottom-3 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-synorio-purple/60 to-transparent"
                initial={{ scaleX: 0, opacity: 0 }}
                whileInView={{ scaleX: 1, opacity: 0.8 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              ></motion.div>
              <motion.div 
                className="absolute -bottom-6 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-transparent via-synorio-purple/30 to-transparent"
                initial={{ scaleX: 0, opacity: 0 }}
                whileInView={{ scaleX: 1, opacity: 0.5 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.5 }}
              ></motion.div>
            </motion.div>
            {subtitle && (
              <motion.p 
                className="text-gray-400 max-w-3xl mx-auto mt-6 px-4"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                {subtitle}
              </motion.p>
            )}
          </div>
        )}
        {children}
      </motion.div>
    </section>
  );
};

export default Section;
